import { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { buildVendorParam } from "../utils/vendorSlug";
import CrawlLink from "../components/CrawlLink";
import VendorDirectoryLinks from "../components/VendorDirectoryLinks";
import { flattenVendorLocations } from "../utils/vendorOutlets";
import { SEOHead } from "../components/SEO/SEOHead";
import styled, { css } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { fetchHealthcareVendors, setSelectedVendor } from "../redux/healthcareSlice";
import { setCurrentHealthcareVendor } from "../redux/healthcareCartSlice";
import Navbar from "../components/Navbar";
// import Footer from "../components/Footer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import HealingIcon from "@mui/icons-material/Healing";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import CircularProgress from "@mui/material/CircularProgress";
import healthcarePlaceholder from "../assets/healthcare_placeholder.png";
import LocationPrompt from "../components/LocationPrompt";
import {
  // DISPLAY-ONLY estimate — used solely as an in-flight fallback while the
  // authoritative backend batch verdict below is loading.
  checkOutletDeliveryAvailability } from "../redux/userLocationSlice";
import useCoverageBatch from "../redux/useCoverageBatch";
import {
  vendorOpenBadge,
  vendorBlocksOrdering,
  describeHours,
  formatNextOpening,
} from "../utils/vendorOpenState";
import { ORDER_AHEAD_LABEL } from "../utils/deliverySchedule";
import { io } from "socket.io-client";
import { COUNTRY_NAME } from "../config/market";
import VendorTagline from "../components/VendorTagline";

const AllHealthcarePage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { healthcareData, isFetching } = useSelector(
    (state) => state.healthcare
  );
  const userLocation = useSelector((state) => state.userLocation);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDelivery, setFilterDelivery] = useState(false);
  const [selectedSpecialization, setSelectedSpecialization] = useState("all");
  const [hoursUpdates, setHoursUpdates] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [socketConnected, setSocketConnected] = useState(false);

  useEffect(() => {
    dispatch(fetchHealthcareVendors());
  }, [dispatch]);

  // Socket.io connection for real-time hours updates
  useEffect(() => {
    let socket = null;
    let isMounted = true;

    const connectSocket = () => {
      try {
        const socketUrl = process.env.REACT_APP_SOCKET_SERVER_URL || "http://localhost:8001";
        socket = io(socketUrl, {
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 5,
          transports: ["websocket", "polling"],
        });

        socket.on("connect", () => {
          if (isMounted) {
            setSocketConnected(true);
          }
        });

        socket.on("disconnect", () => {
          if (isMounted) {
            setSocketConnected(false);
          }
        });

        // Listen for healthcare hours updates
        socket.on("healthcareHoursUpdated", (data) => {
          if (isMounted && data.healthcareId) {
            setHoursUpdates((prev) => ({
              ...prev,
              [data.healthcareId]: {
                isOpen: data.isOpen,
                openHours: data.openHours,
                specialHours: data.specialHours,
                nextOpening: data.nextOpening,
                timezone: data.timezone,
              },
            }));
          }
        });

        // Listen for outlet hours updates
        socket.on("outletHoursUpdated", (data) => {
          if (isMounted && data.outletId) {
            setHoursUpdates((prev) => ({
              ...prev,
              [data.outletId]: {
                isOpen: data.isOpen,
                openHours: data.openHours,
                specialHours: data.specialHours,
                nextOpening: data.nextOpening,
                timezone: data.timezone,
                isOutlet: true,
              },
            }));
          }
        });
      } catch (error) {
        console.warn("Socket connection error:", error);
      }
    };

    connectSocket();

    return () => {
      isMounted = false;
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // The address a card opens, shared by the tap handler and the card's
  // links so the two cannot drift.
  const healthcarePathOf = (location) => {
    if (location.isOutlet) {
      const param = buildVendorParam(
        location.name,
        location.owner || location.healthcareId,
      );
      return `/healthcare/shop/${param}?outletId=${location._id}`;
    }
    const param = buildVendorParam(location.name, location.owner || location._id);
    return `/healthcare/shop/${param}`;
  };

  const handleVendorClick = (location) => {
    if (location.isOutlet) {
      history.push(healthcarePathOf(location));
    } else {
      const vendorId = location.owner || location._id;
      // Blocks only on a vendor we KNOW is shut. A pharmacy that has
      // published no hours keeps trading — the server accepts those orders,
      // and the old `|| "UTC"` calculation used to turn 24/7 and overnight
      // pharmacies into this alert.
      if (vendorBlocksOrdering(location)) {
        alert("This healthcare provider is currently closed. Please try again when they open.");
        return;
      }
      // A pharmacy that is closed now but takes orders for later opens like
      // any other: the card already says "Order ahead" and the storefront
      // leads with the same notice, so interrupting the tap to repeat it just
      // asks the shopper to agree to something they have already read.

      dispatch(setSelectedVendor(location));
      dispatch(setCurrentHealthcareVendor(vendorId));
      history.push(healthcarePathOf(location));
    }
  };

  // Get unique specializations from all vendors
  const allSpecializations = useMemo(() => {
    if (!healthcareData || healthcareData.length === 0) return [];
    const specs = new Set();
    healthcareData.forEach((vendor) => {
      if (vendor.specializations && Array.isArray(vendor.specializations)) {
        vendor.specializations.forEach((spec) => specs.add(spec));
      }
    });
    return Array.from(specs).sort();
  }, [healthcareData]);

  // Healthcare vendors and their outlets as one flat list, every outlet its
  // own card (utils/vendorOutlets, shared with the restaurant and brand
  // listings).
  const allLocations = useMemo(
    () =>
      flattenVendorLocations(healthcareData, {
        outletRow: (vendor, outlet) => ({
          healthcareId: outlet.healthcareId || vendor._id,
          parentHealthcareId: vendor._id,
          parentHealthcareName: vendor.name,
          specializations: vendor.specializations, // Inherit from parent
          tagline: vendor.tagline, // The pharmacy's line, on every branch card
          openHours: outlet.openHours || vendor.openHours,
          specialHours: outlet.specialHours || vendor.specialHours,
          timezone: outlet.timezone || vendor.timezone,
          menuItems: outlet.menuItems || [], // Outlet-specific products
        }),
      }),
    [healthcareData],
  );

  // The id the ORDER pipeline uses for this location — outlets resolve their
  // PARENT vendor (same as handleVendorClick and the checkout gate), so the
  // verdict shown here always matches what checkout will decide.
  const vendorIdFor = useCallback(
    (location) =>
      location.isOutlet
        ? location.owner || location.healthcareId
        : location.owner || location._id,
    []
  );

  // ── AUTHORITATIVE coverage for every listed vendor (one batched call) ────
  // Backend road-distance + radius verdict (POST /api/coverage/batch) — the
  // SAME decision the order-create 422 gate makes. The client haversine stays
  // only as a sorting/label fallback while the batch is in flight.
  const vendorIds = useMemo(
    () => allLocations.map(vendorIdFor).filter(Boolean),
    [allLocations, vendorIdFor]
  );
  const { byVendorId: coverageByVendor } = useCoverageBatch({
    vendorIds,
    enabled: vendorIds.length > 0,
  });

  // One place that answers "does this location deliver / how far?" — backend
  // verdict when we have it, client-side estimate meanwhile.
  const deliveryInfoFor = useCallback(
    (location) => {
      const entry = coverageByVendor[String(vendorIdFor(location))];
      if (entry?.status === "succeeded" && entry.deliverable !== null) {
        return {
          available: entry.deliverable,
          distance: Number.isFinite(Number(entry.distanceKm))
            ? Number(entry.distanceKm)
            : null,
          authoritative: true,
          entry,
        };
      }
      const est = checkOutletDeliveryAvailability(location, userLocation);
      return {
        available: est.available,
        distance: est.distance,
        authoritative: false,
        entry,
      };
    },
    [coverageByVendor, userLocation, vendorIdFor]
  );

  // Filter locations based on search, delivery, and specialization
  const filteredVendors = useMemo(() => {
    let filtered = allLocations.filter((location) => {
      // Search filter
      const matchesSearch =
        location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.specializations?.some((spec) =>
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        );

      // Specialization filter
      const matchesSpec =
        selectedSpecialization === "all" ||
        location.specializations?.includes(selectedSpecialization);

      return matchesSearch && matchesSpec;
    });

    // If filter by delivery is enabled and user has location — prefer the
    // BACKEND verdict (deliveryInfoFor falls back to the client estimate only
    // while the batch is still loading).
    if (filterDelivery && userLocation.latitude && userLocation.longitude) {
      filtered = filtered.filter(
        (location) => deliveryInfoFor(location).available === true
      );
    }

    // Sort by delivery availability if user has location
    if (userLocation.latitude && userLocation.longitude) {
      filtered = [...filtered].sort((a, b) => {
        const aDelivery = deliveryInfoFor(a);
        const bDelivery = deliveryInfoFor(b);

        // Delivering locations first
        if (aDelivery.available && !bDelivery.available) return -1;
        if (!aDelivery.available && bDelivery.available) return 1;

        // Then sort by distance
        return (aDelivery.distance ?? 999) - (bDelivery.distance ?? 999);
      });
    }

    return filtered;
  }, [allLocations, searchQuery, filterDelivery, userLocation, selectedSpecialization, deliveryInfoFor]);

  return (
    <>
      <SEOHead
        title="Healthcare & Pharmacies"
        description={`Order medicines, healthcare products and pharmacy services online. Fast delivery across ${COUNTRY_NAME}.`}
        canonicalPath="/healthcare"
      />
      <Navbar />
      <PageContainer>
        <HeaderSection>
          <BackgroundIcons>
            <IconWrapper style={{ top: "10%", left: "5%", transform: "rotate(-15deg)", "--delay": "0s" }}>
              <LocalHospitalIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "20%", right: "8%", transform: "rotate(20deg)", "--delay": "0.5s" }}>
              <MedicalServicesIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "50%", left: "3%", transform: "rotate(-25deg)", "--delay": "1s" }}>
              <LocalPharmacyIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "60%", right: "5%", transform: "rotate(15deg)", "--delay": "1.5s" }}>
              <HealthAndSafetyIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "30%", left: "12%", transform: "rotate(10deg)", "--delay": "2s" }}>
              <HealingIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "70%", left: "15%", transform: "rotate(-10deg)", "--delay": "2.5s" }}>
              <MonitorHeartIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "40%", right: "12%", transform: "rotate(-20deg)", "--delay": "1.2s" }}>
              <LocalHospitalIcon />
            </IconWrapper>
            <IconWrapper style={{ top: "15%", left: "30%", transform: "rotate(25deg)", "--delay": "0.8s" }}>
              <LocalPharmacyIcon />
            </IconWrapper>
          </BackgroundIcons>
          <HeaderContent>
            <Title>Health & Wellness</Title>
            <Subtitle>
              Your trusted healthcare partners for medicines and wellness products!
            </Subtitle>
            <SearchBar>
              <SearchInput
                type="text"
                placeholder="Search pharmacies, medicines, or specializations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
            <FilterRow>
              <LocationPrompt compact />
              {userLocation.latitude && userLocation.longitude && (
                <DeliveryFilterButton
                  $active={filterDelivery}
                  onClick={() => setFilterDelivery(!filterDelivery)}
                >
                  {filterDelivery ? "Show All" : "Delivers to Me"}
                </DeliveryFilterButton>
              )}
            </FilterRow>
            {allSpecializations.length > 0 && (
              <SpecializationFilter>
                <SpecButton
                  $active={selectedSpecialization === "all"}
                  onClick={() => setSelectedSpecialization("all")}
                >
                  All
                </SpecButton>
                {allSpecializations.slice(0, 6).map((spec) => (
                  <SpecButton
                    key={spec}
                    $active={selectedSpecialization === spec}
                    onClick={() => setSelectedSpecialization(spec)}
                  >
                    {spec}
                  </SpecButton>
                ))}
              </SpecializationFilter>
            )}
          </HeaderContent>
        </HeaderSection>

        <ContentSection>
          {isFetching ? (
            <LoadingContainer>
              <CircularProgress style={{ color: "#1976d2" }} size={50} />
              <LoadingText>Loading healthcare providers...</LoadingText>
            </LoadingContainer>
          ) : filteredVendors.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>💊</EmptyStateIcon>
              <EmptyStateTitle>No Healthcare Providers Found</EmptyStateTitle>
              <EmptyStateText>
                {searchQuery || selectedSpecialization !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "No healthcare providers available at the moment"}
              </EmptyStateText>
            </EmptyState>
          ) : (
            <>
              <ResultsCount>
                Showing {filteredVendors.length} healthcare provider
                {filteredVendors.length !== 1 ? "s" : ""}
                {filterDelivery && " that deliver to your location"}
              </ResultsCount>
              <VendorsGrid>
                {filteredVendors.map((vendor) => {
                  const vendorId = vendor.healthcareId || vendor._id;
                  const vendorWithUpdates = {
                    ...vendor,
                    ...hoursUpdates[vendorId],
                  };
                  const statusBadge = vendorOpenBadge(vendorWithUpdates);
                  const isOpen = statusBadge?.tone === "open";
                  // Closed now, taking orders for later: an "Order ahead" pill
                  // in place of "Closed", and no dimming overlay.
                  const orderAhead = statusBadge?.tone === "scheduled";

                  const deliveryInfo = userLocation.latitude && userLocation.longitude
                    ? deliveryInfoFor(vendor)
                    : null;
                  // Only an explicit false marks the card out-of-range —
                  // null (undecidable / still checking) must not grey it out.
                  const isOutOfRange = deliveryInfo?.available === false;

                  return (
                    <VendorCard
                      key={vendor._id}
                      onClick={() => handleVendorClick(vendor)}
                      $isOpen={isOpen || orderAhead}
                      $isOutOfRange={isOutOfRange}
                    >
                      <CrawlLink
                        to={healthcarePathOf(vendor)}
                        onNavigate={() => handleVendorClick(vendor)}
                        style={{ display: "block" }}
                      >
                        <CardHeader $isLogoFallback={!vendor.bannerUrl && !!vendor.imageUrl}>
                          <LogoWrapper>
                            {vendor.bannerUrl ? (
                              <Logo src={vendor.bannerUrl} alt={vendor.name} />
                            ) : vendor.imageUrl ? (
                              <Logo
                                src={vendor.imageUrl}
                                alt={vendor.name}
                                $isLogoFallback
                              />
                            ) : (
                              <PlaceholderLogo>
                                <img src={healthcarePlaceholder} alt="Healthcare placeholder" />
                              </PlaceholderLogo>
                            )}
                          </LogoWrapper>
                          <StatusBadge $isOpen={isOpen} $orderAhead={orderAhead}>
                            {orderAhead ? ORDER_AHEAD_LABEL : isOpen ? "Open" : "Closed"}
                          </StatusBadge>
                          {isOutOfRange && (
                            <OutOfRangeBadge title={`${deliveryInfo.distance?.toFixed(1) || '?'}km away - Outside delivery range`}>
                              Doesn't deliver to {userLocation.address || userLocation.city || "your location"}
                            </OutOfRangeBadge>
                          )}
                        </CardHeader>
                      </CrawlLink>

                      <CardContent>
                        <CardHeading>
                          <VendorName>
                            <CrawlLink
                              to={healthcarePathOf(vendor)}
                              onNavigate={() => handleVendorClick(vendor)}
                            >
                              {vendor.name}
                            </CrawlLink>
                          </VendorName>
                          <VerifiedTick title="Verified Provider" aria-label="Verified Provider">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                              <circle cx="12" cy="12" r="11" fill="#10b981" />
                              <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </VerifiedTick>
                        </CardHeading>
                        <CardTagline vendor={vendor} />

                        {(() => {
                          const activeOutlets = vendor.outlets?.filter(o => o.isActive !== false) || [];
                          const firstSpec = vendor.specializations?.[0];
                          const hasMeta = firstSpec || activeOutlets.length > 0;
                          if (!hasMeta) return null;
                          return (
                            <MetaRow>
                              {firstSpec && <SpecTag>{firstSpec}</SpecTag>}
                              {activeOutlets.length > 0 && (
                                <SpecTag $muted>
                                  {activeOutlets.length} outlet{activeOutlets.length > 1 ? "s" : ""}
                                </SpecTag>
                              )}
                            </MetaRow>
                          );
                        })()}

                        <InfoRow>
                          <LocationOnIcon style={{ fontSize: 15, color: "#e91e63" }} />
                          <AddressText>{vendor.address || "Location not specified"}</AddressText>
                        </InfoRow>

                        {/* Compact open/closed status row */}
                        {statusBadge && (() => {
                          const hoursText = describeHours(statusBadge.state);
                          const next = formatNextOpening(statusBadge.state.nextOpening);
                          return (
                            <HoursPill $isOpen={isOpen} $orderAhead={orderAhead}>
                              <HoursDot $isOpen={isOpen} $orderAhead={orderAhead} />
                              <HoursLabel $isOpen={isOpen} $orderAhead={orderAhead}>
                                {orderAhead ? ORDER_AHEAD_LABEL : isOpen ? "Open" : "Closed"}
                              </HoursLabel>
                              <HoursSep>·</HoursSep>
                              <HoursTimes>{isOpen ? hoursText : next || hoursText}</HoursTimes>
                            </HoursPill>
                          );
                        })()}

                        {(() => {
                          const activeOutlets = vendor.outlets?.filter(o => o.isActive !== false) || [];
                          const radius = activeOutlets[0]?.deliveryZoneRadius || vendor.deliveryZoneRadius;
                          return radius ? (
                            <DeliveryHint>
                              <span style={{ marginRight: 6 }}>📦</span>
                              Delivers within {radius} km
                            </DeliveryHint>
                          ) : null;
                        })()}
                      </CardContent>

                      <CardFooter>
                        <ViewButton>Browse products →</ViewButton>
                      </CardFooter>

                      {!isOpen && !orderAhead && <ClosedOverlay>Closed</ClosedOverlay>}
                    </VendorCard>
                  );
                })}
              </VendorsGrid>
            </>
          )}
        </ContentSection>
        {/* Every healthcare storefront, including the ones the cards above leave out. */}
        <VendorDirectoryLinks vertical="healthcare" title="All pharmacies and health stores on Lusuku" />
      </PageContainer>
    </>
  );
};

export default AllHealthcarePage;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  overflow-x: hidden;
  max-width: 100vw;
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, #fdf2f6 0%, #fce4ec 100%);
  padding: 1.25rem 1.5rem 1.75rem;
  padding-top: calc(var(--lusuku-nav-h, 130px) + 1rem);
  position: relative;
  overflow: hidden;
  margin-bottom: 1rem;
  z-index: 10;
  box-sizing: border-box;
  width: 100%;
  border-bottom: 1px solid rgba(233, 30, 99, 0.08);

  @media (max-width: 850px) {
    padding-top: calc(var(--lusuku-nav-h, 130px) + 0.75rem);
  }
`;

const BackgroundIcons = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.08;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  position: absolute;
  font-size: 2rem;
  color: #e91e63;
  animation: float 6s ease-in-out infinite;
  animation-delay: var(--delay, 0s);

  @keyframes float {
    0% {
      transform: translateY(0) rotate(var(--rotation, 0deg));
    }
    50% {
      transform: translateY(-10px) rotate(var(--rotation, 0deg));
    }
  }

  svg {
    font-size: inherit;
  }
`;

const HeaderContent = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  padding: 0 1rem;
`;

const Title = styled.h1`
  color: #1a1a2e;
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 0.25rem;
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const Subtitle = styled.p`
  color: #c2185b;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const SearchBar = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 12px auto 0;
  box-sizing: border-box;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const DeliveryFilterButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${(props) => (props.$active ? "#e91e63" : "#f3d6e1")};
  background: ${(props) => (props.$active ? "#e91e63" : "white")};
  color: ${(props) => (props.$active ? "white" : "#666")};

  &:hover {
    border-color: #e91e63;
    background: ${(props) => (props.$active ? "#c2185b" : "#fce4ec")};
  }
`;

const SpecializationFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const SpecButton = styled.button`
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.$active ? "#e91e63" : "#f3d6e1")};
  background: ${(props) => (props.$active ? "#e91e63" : "white")};
  color: ${(props) => (props.$active ? "white" : "#555")};

  &:hover {
    border-color: #e91e63;
    background: ${(props) => (props.$active ? "#c2185b" : "#fce4ec")};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px;
  border: 2px solid #f8bbd0;
  border-radius: 12px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  font-family: "Inter", sans-serif;
  background: white;
  box-shadow: 0 2px 8px rgba(233, 30, 99, 0.08);
  box-sizing: border-box;

  &:focus {
    border-color: #e91e63;
    box-shadow: 0 0 0 3px rgba(233, 30, 99, 0.15);
  }

  &::placeholder {
    color: #999;
  }
`;

const ContentSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem 3rem 1rem;
  position: relative;
  overflow: visible;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  gap: 1rem;
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 1rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h2`
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const EmptyStateText = styled.p`
  color: #666;
  font-size: 1rem;
`;

const ResultsCount = styled.p`
  color: #666;
  font-size: 0.95rem;
  // margin-bottom: 1rem;
`;

const VendorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  padding: 1rem 0 2rem;
  position: relative;
  z-index: 1;
`;

const VendorCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 2px 10px rgba(233, 30, 99, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(233, 30, 99, 0.08);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(233, 30, 99, 0.18),
      0 3px 8px rgba(233, 30, 99, 0.08);
  }

  ${(props) =>
    !props.$isOpen &&
    css`
      opacity: 0.75;
    `}

  ${(props) =>
    props.$isOutOfRange &&
    css`
      opacity: 0.6;
      border-color: #fc4d4de6;
      background: #FFFBEB;
    `}
`;

/* Unused styled component - kept for potential future use
const CardGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1976d2, #42a5f5, #1976d2);
  opacity: 0;
  transition: opacity 0.3s ease;

  ${VendorCard}:hover & {
    opacity: 1;
  }
`;
*/

const LogoWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => (props.$isLogoFallback ? "contain" : "cover")};
  padding: ${(props) => (props.$isLogoFallback ? "14px" : "0")};
  display: block;
`;

const PlaceholderLogo = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fdf2f6 0%, #fce4ec 50%, #f8bbd0 100%);
  position: relative;

  img {
    width: 70%;
    height: 70%;
    object-fit: contain;
    filter: drop-shadow(0 4px 10px rgba(194, 24, 91, 0.18));
  }
`;

const VendorName = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  letter-spacing: -0.2px;
`;

const CardHeader = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: ${(props) =>
    props.$isLogoFallback
      ? "linear-gradient(135deg, #fdf2f6 0%, #fce4ec 100%)"
      : "#fdf2f6"};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: ${(props) =>
    props.$isOpen ? "#10b981" : props.$orderAhead ? "#e0e9ff" : "#fee2e2"};
  color: ${(props) =>
    props.$isOpen ? "#ffffff" : props.$orderAhead ? "#1d4ed8" : "#991b1b"};
  box-shadow: 0 2px 6px ${(props) =>
    props.$isOpen
      ? "rgba(16, 185, 129, 0.3)"
      : props.$orderAhead
        ? "rgba(29, 78, 216, 0.2)"
        : "rgba(239, 68, 68, 0.2)"};
`;

const OutOfRangeBadge = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #fec7c7ff;
  color: #000000;
  border: 1px solid #FCD34D;
  cursor: help;
  transition: all 0.2s ease;

  &:hover {
    background: #FDE68A;
    transform: scale(1.05);
  }
`;

const CardContent = styled.div`
  flex: 1;
  padding: 0.85rem 1.1rem 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

/* The vendor's own tagline under its name. Nothing renders when the vendor
   has not written one (components/VendorTagline.jsx). */
const CardTagline = styled(VendorTagline)`
  margin: 2px 0 8px;
`;

const CardHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VerifiedTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const DeliveryHint = styled.div`
  font-size: 0.75rem;
  color: #475569;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
`;

const CardFooter = styled.div`
  padding: 0.7rem 1.1rem 0.85rem;
  border-top: 1px solid #f3e5ed;
  display: flex;
`;

const ViewButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, #e91e63, #c2185b);
  color: white;
  border: none;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  letter-spacing: 0.01em;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  box-shadow: 0 2px 8px rgba(233, 30, 99, 0.18);

  &:hover {
    box-shadow: 0 6px 16px rgba(233, 30, 99, 0.32);
    filter: brightness(1.05);
  }

  &:active {
    transform: translateY(1px);
  }
`;

/* Inline compact open/closed pill replacing the bulky WorkingHoursDisplay */
const HoursPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: ${(props) =>
    props.$isOpen
      ? "rgba(16, 185, 129, 0.10)"
      : props.$orderAhead
        ? "rgba(29, 78, 216, 0.10)"
        : "rgba(239, 68, 68, 0.10)"};
  border: 1px solid ${(props) =>
    props.$isOpen
      ? "rgba(16, 185, 129, 0.25)"
      : props.$orderAhead
        ? "rgba(29, 78, 216, 0.25)"
        : "rgba(239, 68, 68, 0.25)"};
  border-radius: 999px;
  width: fit-content;
  font-size: 0.75rem;
`;

const HoursDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(props) =>
    props.$isOpen ? "#10b981" : props.$orderAhead ? "#1d4ed8" : "#ef4444"};
  box-shadow: 0 0 0 3px
    ${(props) =>
      props.$isOpen
        ? "rgba(16, 185, 129, 0.18)"
        : props.$orderAhead
          ? "rgba(29, 78, 216, 0.18)"
          : "rgba(239, 68, 68, 0.15)"};
  flex-shrink: 0;
`;

const HoursLabel = styled.span`
  font-weight: 700;
  color: ${(props) =>
    props.$isOpen ? "#047857" : props.$orderAhead ? "#1d4ed8" : "#b91c1c"};
`;

const HoursSep = styled.span`
  color: #94a3b8;
  margin: 0 2px;
`;

const HoursTimes = styled.span`
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpecTag = styled.span`
  padding: 3px 10px;
  background: ${(props) => (props.$muted ? "#f1f5f9" : "#fff")};
  border: 1px solid ${(props) => (props.$muted ? "#e2e8f0" : "#f8bbd0")};
  border-radius: 999px;
  font-size: 0.72rem;
  color: ${(props) => (props.$muted ? "#475569" : "#c2185b")};
  font-weight: 600;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #666;
  font-size: 0.85rem;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const AddressText = styled.span`
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
  color: #475569;
`;


const ClosedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  pointer-events: none;
`;
